import { createBrowserRouter, Navigate, generatePath, Outlet, RouterProvider } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { DashboardLayout } from '@/ui-kit/templates/DashboardLayout';
import { EmptyLayout } from '@/ui-kit/templates/EmptyLayout';
import { BulkCardCreation } from '@/features/cards/components/bulkCardCreation/BulkCardCreation';
import { BulkStepper } from '@/features/cards/components/bulkCardCreation/BulkStepper';
import { OnboardingUsersPage } from '@/pages/admin/onboarding/OnboardingUsers';
import { RestrictionGroupMetadata } from '@/pages/admin/restrictionGroup/RestrictionGroupMetadata';
import { RestrictionsGroupTab } from '@/pages/admin/restrictionGroup/RestrictionsGroupTab';
import { ConfigurationPage } from '@/pages/admin/configuration/ConfigurationPage';
import { DashboardConfiguration } from '@/pages/admin/configuration/DashboardConfiguration';
import { Integration } from '@/pages/admin/configuration/Integration';
import { Security } from '@/pages/admin/configuration/Security';
import { Cryptography } from '@/pages/admin/cryptography/Cryptography';
import { DashboardUsers } from '@/features/dashboard-users/components/DashboardUsers';
import { KycNeedReview } from '@/pages/admin/kyc-need-review/KycNeedReviewPage';
import { RestrictionsGroupsPage } from '@/pages/admin/restrictionGroup/RestrictionsGroupsPage';
import TemplatesPage from '@/pages/admin/template/TemplatesPage';
import { Webhooks } from '@/features/webhooks/components/Webhooks';
import { SignIn } from '@/pages/auth/SignIn';
import { SupportEnterPassphrase } from '@/pages/auth/SupportEnterPassphrase';
import Page2Fa from '@/pages/auth/Page2Fa';
import { AllCardTransactions } from '@/features/cards/components/AllCardTransactions';
import { PaymentCardsTab } from '@/features/cards/components/PaymentCardsTab';
import { Profiles } from '@/app/dashboard/user-profile/page';
import { Profile } from '@/features/user/components/Profile';
import { AllUserBeneficiaries } from '@/features/beneficiaries/components/UserBeneficiary/AllUserBeneficiaries';
import { WalletsTab } from '@/features/wallets/components/WalletsTab';
import { withAuth } from '@/ui-kit/templates/withAuth';
import { withProfilePath } from '@/app/dashboard/user-profile/withProfilePath';
import { withTabs } from '@/ui-kit/templates/withTabs';
import NoAccessPage from '@/pages/NoAccessPage';
import Overview from '@/app/dashboard/user-profile/overview/page';
import KycKyb from '@/app/dashboard/user-profile/kyc-kyb/page';
import CompanyMembers from '@/app/dashboard/user-profile/company-members/page';
import { useRoutepaths } from '@/router/hooks/useRoutePaths';
import { CardsPage } from '@/app/dashboard/cards/page';
import { WalletsPage } from '@/app/dashboard/Wallets/page';
import { CreateUserPage } from '@/app/dashboard/create-user/page';
import { useApplicationTheme } from '@/hooks/useApplicationTheme';
import { UserPage } from '@/app/dashboard/users/page';
import { Loader } from '@/ui-kit/loader/Loader';
import { BeneficiariesPage } from '@/app/dashboard/beneficiaries/page';
import { AppPage } from '@/app/page';
import { BubbleErrors } from '@/router/components/BubbleErrors';
import { DashboardUserDetail } from '@/features/dashboard-users/components/detail/DashboardUserDetail';

export const DashboardRoutes = () => {
  const PathRoute = useRoutepaths();
  const { config } = useApplicationTheme();
  const { t } = useTranslation();

  const routes =
    PathRoute && config
      ? [
          { path: PathRoute.all, element: <Navigate to={PathRoute.auth.root} /> },
          {
            element: <AppPage />,
            errorElement: <BubbleErrors />,
            children: [
              {
                path: PathRoute.auth.root,
                element: <EmptyLayout />,
                children: [
                  {
                    path: '*',
                    element: <Navigate to={generatePath(PathRoute.auth.root, { '*': PathRoute.auth.signIn })} />,
                  },
                  { path: PathRoute.auth.signIn, element: <SignIn /> },
                  { path: PathRoute.auth.passphrase, element: <SupportEnterPassphrase /> },
                  { path: PathRoute.auth.role, element: <NoAccessPage /> },
                  { path: PathRoute.auth['2fa'], element: <Page2Fa /> },
                ],
              },
              {
                path: PathRoute.app.root,
                element: withAuth(DashboardLayout),
                children: [
                  {
                    path: PathRoute.app.users.main,
                    element: <Outlet />,
                    children: [
                      { index: true, element: <UserPage /> },
                      {
                        path: PathRoute.app.users.profile,
                        element: withProfilePath(Profiles),
                        children: [
                          {
                            path: '',
                            element: (
                              <Profile>
                                <div className="overflow-y-auto">
                                  <Outlet />
                                </div>
                              </Profile>
                            ),
                            children: [
                              { index: true, element: <Navigate to={PathRoute.app.users.tabs.overview} replace /> },
                              { path: PathRoute.app.users.tabs.overview, element: <Overview /> },
                              { path: PathRoute.app.users.tabs.kyckyb, element: <KycKyb /> },
                              { path: PathRoute.app.users.tabs.children, element: <CompanyMembers /> },
                              { path: PathRoute.app.users.tabs.companyMembers, element: <CompanyMembers /> },
                              {
                                path: PathRoute.app.users.tabs.wallets.main,
                                element: <WalletsTab />,
                                children: [{ path: PathRoute.app.users.tabs.wallets.wallet, element: <WalletsTab /> }],
                              },
                              {
                                path: PathRoute.app.users.tabs.cards.main,
                                element: <PaymentCardsTab />,
                                children: [{ path: PathRoute.app.users.tabs.cards.card, element: <PaymentCardsTab /> }],
                              },
                              {
                                path: PathRoute.app.users.tabs.beneficiaries.main,
                                element: <AllUserBeneficiaries />,
                                children: [
                                  {
                                    path: PathRoute.app.users.tabs.beneficiaries.beneficiaryId,
                                    element: <AllUserBeneficiaries />,
                                  },
                                ],
                              },
                            ],
                          },
                        ],
                      },
                    ],
                  },
                  { path: PathRoute.app.users.main, element: <UserPage /> },
                  { path: PathRoute.app.createUser, element: <CreateUserPage /> },
                  { path: PathRoute.app.cards, element: <CardsPage /> },
                  { path: PathRoute.app.wallets, element: <WalletsPage /> },
                  { path: PathRoute.app.beneficiaries, element: <BeneficiariesPage /> },
                  { path: PathRoute.app.cardTransactions, element: <AllCardTransactions /> },
                  { path: PathRoute.app.kycNeedReview, element: <KycNeedReview /> },
                  {
                    path: PathRoute.app.bulkCards.main,
                    children: [
                      {
                        path: '*',
                        element: (
                          <Navigate
                            to={generatePath(PathRoute.app.root, {
                              '*': generatePath(PathRoute.app.bulkCards.others, {
                                '*': PathRoute.app.bulkCards.orders,
                              }),
                            })}
                          />
                        ),
                      },
                      { path: PathRoute.app.bulkCards.orders, element: <BulkCardCreation /> },
                      { path: PathRoute.app.bulkCards.create, element: <BulkStepper /> },
                    ],
                  },
                  { path: PathRoute.app.onboarding, element: <OnboardingUsersPage /> },
                  { path: PathRoute.app.dashboardUsers, element: <DashboardUsers /> },
                  { path: PathRoute.app.accountOverview, element: <DashboardUserDetail /> },
                  { path: PathRoute.app.templates, element: <TemplatesPage /> },
                  { path: PathRoute.app.webhooks, element: <Webhooks /> },
                  {
                    path: PathRoute.app.restrictions.main,
                    element: withTabs(
                      RestrictionsGroupsPage,
                      config.settings.cards.restrictions?.map((type) => ({
                        id: type,
                        path: type,
                        label: t(`pages.administration.restrictionGroup.tabs.${type}`),
                      })) ?? [],
                    ),
                    children: [
                      {
                        path: '*',
                        element: (
                          <Navigate
                            to={
                              config.settings.cards.restrictions?.length
                                ? `../${PathRoute.app.restrictions.tabs[`${config.settings.cards.restrictions[0]}`]}`
                                : `../${PathRoute.app.restrictions.main}`
                            }
                          />
                        ),
                      },
                      ...(config.settings.cards.restrictions?.length
                        ? [{ path: PathRoute.app.restrictions.restrictionType, element: <RestrictionsGroupTab /> }]
                        : []),
                      { path: PathRoute.app.restrictions.details, element: <RestrictionGroupMetadata /> },
                    ],
                  },
                  { path: PathRoute.app.encryption, element: <Cryptography /> },
                  {
                    path: PathRoute.app.configuration.main,
                    element: withTabs(
                      ConfigurationPage,
                      config.settings.configuration?.tabs?.map((type) => ({
                        id: type,
                        path: PathRoute.app.configuration.tabs[type],
                        label: t(`pages.administration.configuration.tabs.${type}`),
                      })) ?? [],
                      { size: 's' },
                    ),
                    children: [
                      {
                        path: '*',
                        element: (
                          <Navigate
                            to={generatePath(PathRoute.app.root, {
                              '*': generatePath(PathRoute.app.configuration.others, {
                                '*': PathRoute.app.configuration.tabs.settings,
                              }),
                            })}
                          />
                        ),
                      },
                      { path: PathRoute.app.configuration.tabs.settings, element: <DashboardConfiguration /> },
                      { path: PathRoute.app.configuration.tabs.integration, element: <Integration /> },
                      { path: PathRoute.app.configuration.tabs.security, element: <Security /> },
                    ],
                  },
                ],
              },
            ],
          },
        ]
      : undefined;

  return routes ? <RouterProvider router={createBrowserRouter(routes)} /> : <Loader />;
};
